<template>
  <div
    class="rs-checkbox"
    :class="checkboxClasses"
  >
    <input
      :id="`check_${id}`"
      type="checkbox"
      :name="name"
      :checked="shouldBeChecked"
      :value="value"
      :disabled="disabled"
      @change="updateInput"
    >
    <label
      class="d-flex align-items-center cursor-pointer mb-0"
      :for="'check_' + id"
    >
      <div
        class="rs-checkbox-icon"
        :class="{ disabled, checked: shouldBeChecked }"
      >
        <i class="fas fa-check" />
      </div>
      <span
        v-if="label"
        class="label ml-2 mb-0 mr-2"
        :class="{ 'text-white': setLabelWhite, 'label-pill': labelColor && labelBackgroundColor }"
        :style="labelColor && labelBackgroundColor ? { color: labelColor, backgroundColor: labelBackgroundColor } : {}"
      >{{ label }}</span>
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

type TCheckboxValue = boolean | number | string | unknown[] | null | undefined;

// Credit: https://www.smashingmagazine.com/2017/08/creating-custom-inputs-vue-js/
export default defineComponent({
  name: 'RsFormCheckbox',
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Object, Number, String, Boolean],
      default: null,
    },
    modelProperty: {
      type: String,
      default: null,
    },
    modelValue: {
      type: [Boolean, Number, String, Array] as PropType<TCheckboxValue>,
      default: false,
    },
    setLabelWhite: {
      type: Boolean,
      default: false,
    },
    trueValue: {
      type: [Boolean, Number, String, Array] as PropType<TCheckboxValue>,
      default: true,
    },
    falseValue: {
      type: [Boolean, Number, String, Array] as PropType<TCheckboxValue>,
      default: false,
    },
    checkboxClasses: {
      type: String,
      default: '',
    },
    /**
     * Optional pill styling for the label. Both labelColor
     * and labelBackgroundColor must be set for the pill style to apply.
    */
    labelColor: {
      type: String,
      default: '',
    },
    labelBackgroundColor: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    shouldBeChecked(): boolean {
      if (this.modelValue instanceof Array) {
        return this.modelValue.some((v: any) => {
          if (this.modelProperty) {
            return v[this.modelProperty] === (this.value as Record<string, any>)[this.modelProperty];
          }

          if (v?.id && (this.value as Record<string, any>)?.id) {
            return `${v.id}` === `${(this.value as Record<string, any>).id}`;
          }

          if (v?.value === this.value && v?.selected !== null && v?.selected !== undefined) {
            return v.selected;
          }

          return v?.value ? `${v.value}` === `${this.value}` : `${v}` === `${this.value}`;
        });
      }
      return this.modelValue === this.trueValue || false;
    },
  },
  methods: {
    updateInput(event: Event): void {
      const isChecked = (event.target as HTMLInputElement).checked;

      if (this.modelValue instanceof Array) {
        const newValue = [...this.modelValue];

        if (isChecked) {
          newValue.push(this.value);
        } else {
          const idx = newValue.findIndex((v: any) => {
            if (this.modelProperty) {
              return v[this.modelProperty] === (this.value as Record<string, any>)[this.modelProperty];
            }

            if (v?.id) {
              return `${v.id}` === `${(this.value as Record<string, any>).id}`;
            }

            return v?.value ? `${v.value}` === `${this.value}` : `${v}` === `${this.value}`;
          });

          if (idx !== -1) {
            newValue.splice(idx, 1);
          }
        }
        this.$emit('update:modelValue', newValue);
        this.$emit('change', newValue);
      } else {
        const next = isChecked ? this.trueValue : this.falseValue;
        this.$emit('update:modelValue', next);
        this.$emit('change', next);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-checkbox{
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;
  .label{
    font-weight: normal;
    color: $tertiary-muted;
  }
  .label-pill {
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 12px;
  }

  .rs-checkbox-icon {
    border: solid 1px $gray-400;
    background: $white;
    min-width: 18px;
    width: 18px;
    height: 18px;
    border-radius: 3px;
    display: block;
    color: $white;
    line-height: 1;
    position: relative;
    transition: all ease-in-out 150ms;

    i {
      position: absolute;
      top: 3px;
      left: 0;
      right: 0;
      bottom: 0;
      font-size: 10px;
      opacity: 0;
      transition: opacity ease-in-out 200ms;
      text-align: center;
    }

    &:hover {
      border-color: $secondary-color;
    }

    &.checked {
      background: $secondary-color;
      border-color: $secondary-color;

      i {
        opacity: 1;
      }
    }

    &.disabled {
      border-color: $tertiary-gray;
      background-color: rgba($tertiary-gray, 0.3);
    }
  }

  input[type="checkbox"] {
    display: none !important;
  }
}
</style>
